import { ReactNode, useCallback, useRef, useState } from "react";
import clsx from "clsx";
import { base, navigation } from "@k8slens/lds-icons";

import DrawerTransition from "src/components/DrawerTransition/DrawerTransition";

import styles from "./Notification.module.css";

export type Type = "flash" | "closable" | "inline" | "default";
export type AlertLevel = "info" | "success" | "error" | "warning";

export interface Props {
  message: string | ReactNode;
  title?: string;
  type?: Type;
  level?: AlertLevel;
  inline?: false;
  flash?: false;
  /**
   * Hide after `flashDuration` milliseconds, if type is set to "flash"
   */
  flashDuration?: number;
  /**
   * Can be set closable if not inline
   */
  closable?: boolean;
  onClose?(): void;
  className?: string;
}

const { CloseIcon } = navigation;
const { SuccessIcon, WarningIcon, InfoIcon } = base;

const Notification: React.FC<Props> = ({
  level = "info",
  type = "default",
  title,
  message,
  flashDuration = 5000,
  onClose,
  className,
}) => {
  const [show, setShow] = useState<boolean>(true);
  const contentRef = useRef<null | HTMLDivElement>(null);

  const duration = type === "flash" ? 300 : 200;

  const handleAfterEnter = useCallback(() => {
    if (type === "flash") {
      // Close after automatically
      setTimeout(() => {
        try {
          setShow(false);
        } catch (err) {
          // ignore
        }
      }, flashDuration);
    }
  }, [type, flashDuration]);

  return (
    <DrawerTransition
      open={show}
      role="alert"
      className={clsx(className, styles.wrapper, styles[level], styles[type])}
      afterEnter={handleAfterEnter}
      afterExit={onClose}
      duration={duration}
    >
      <div ref={contentRef} className={styles.alert}>
        {level === "success" && <SuccessIcon size="xl" />}
        {level === "info" && <InfoIcon size="xl" />}
        {(level === "error" || level === "warning") && <WarningIcon size="xl" />}
        <div>
          {title && <h5>{title}</h5>}
          <p>{message}</p>
        </div>
        {type === "closable" && (
          <button type="button" onClick={() => setShow(false)} aria-label="Close">
            <CloseIcon size="lg" color="on-dark" />
          </button>
        )}
      </div>
    </DrawerTransition>
  );
};

export default Notification;
